import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";

export interface ReceiptPdfLabels {
  invoiceNumber: string;
  orderNumber: string;
  amountPaid: string;
  paymentDate: string;
  paymentMethod: string;
  transactionId: string;
  status: string;
}

export interface ReceiptPdfData {
  title: string;
  subtitle: string;
  paymentSuccessful: string;
  invoiceNumber: string;
  orderNumber: string;
  amountPaid: string;
  paymentDate: string;
  paymentMethod: string;
  transactionId: string;
  status: string;
  thankYou: string;
  companyName: string;
  labels: ReceiptPdfLabels;
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function buildReceiptElement(data: ReceiptPdfData, isRTL: boolean): HTMLDivElement {
  const dir = isRTL ? "rtl" : "ltr";
  const statusRaw = String(data.status ?? "").trim().toLowerCase();
  const isPaid =
    !statusRaw ||
    statusRaw === "paid" ||
    statusRaw.includes("paid") ||
    statusRaw.includes("success") ||
    statusRaw.includes("completed") ||
    statusRaw.includes("مدفوع");
  const badgeBg = isPaid ? "rgba(22,163,74,0.1)" : "rgba(220,38,38,0.1)";
  const badgeColor = isPaid ? "#16a34a" : "#dc2626";
  const badgeBorder = isPaid ? "rgba(22,163,74,0.2)" : "rgba(220,38,38,0.2)";

  const rows: Array<[string, string]> = [
    [data.labels.invoiceNumber, data.invoiceNumber || "—"],
    [data.labels.orderNumber, data.orderNumber || "—"],
    [data.labels.amountPaid, data.amountPaid || "—"],
    [data.labels.paymentDate, data.paymentDate || "—"],
    [data.labels.paymentMethod, data.paymentMethod || "—"],
    [data.labels.transactionId, data.transactionId || "—"],
  ];

  const rowHtml = rows
    .map(
      ([label, value]) => `
      <div style="display:flex;justify-content:space-between;align-items:baseline;gap:16px;padding:10px 4px;border-bottom:1px solid rgba(226,232,240,0.6);">
        <span style="flex:0 0 auto;white-space:nowrap;letter-spacing:normal;word-spacing:normal;text-align:start;font-size:13px;font-weight:500;color:#64748b;">${escapeHtml(label)}</span>
        <span style="flex:1;min-width:0;font-size:13px;font-weight:500;color:#1a2332;text-align:end;direction:ltr;unicode-bidi:isolate;">${escapeHtml(value)}</span>
      </div>`
    )
    .join("");

  const container = document.createElement("div");
  container.dir = dir;
  container.lang = isRTL ? "ar" : "en";
  container.style.cssText = [
    "position:fixed",
    "left:-10000px",
    "top:0",
    "width:794px",
    "box-sizing:border-box",
    "padding:48px 56px",
    "background:#ffffff",
    "font-family:'Arimo', 'Noto Sans Arabic', 'Segoe UI', Tahoma, sans-serif",
    "color:#1a2332",
  ].join(";");

  container.innerHTML = `
    <div style="text-align:center;margin-bottom:24px;">
      <h1 style="margin:0 0 8px;font-size:22px;font-weight:700;color:#1a2332;">${escapeHtml(data.title)}</h1>
      <p style="margin:0;font-size:14px;color:#64748b;">${escapeHtml(data.subtitle)}</p>
    </div>
    <div style="text-align:center;margin-bottom:20px;">
      <div style="display:inline-flex;align-items:center;justify-content:center;width:56px;height:56px;border-radius:50%;background:rgba(22,163,74,0.08);margin-bottom:12px;">
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M5 13l4 4L19 7" stroke="#00A63E" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </div>
      <div style="font-size:16px;font-weight:600;color:#1a2332;">${escapeHtml(data.paymentSuccessful)}</div>
    </div>
    <div style="border-top:1px solid rgba(226,232,240,0.8);padding-top:8px;margin-bottom:8px;">
      ${rowHtml}
      <div style="display:flex;justify-content:space-between;align-items:center;padding:10px 4px;">
        <span style="font-size:13px;font-weight:500;color:#64748b;">${escapeHtml(data.labels.status)}</span>
        <span style="display:inline-block;padding:4px 10px;border-radius:999px;font-size:12px;font-weight:600;background:${badgeBg};color:${badgeColor};border:1px solid ${badgeBorder};">${escapeHtml(data.status || "Paid")}</span>
      </div>
    </div>
    <div style="margin-top:20px;padding:16px;background:rgba(248,249,250,0.8);border:1px solid rgba(226,232,240,0.5);border-radius:10px;text-align:center;">
      <p style="margin:0 0 4px;font-size:13px;color:#94a3b8;">${escapeHtml(data.thankYou)}</p>
      <p style="margin:0;font-size:13px;color:#94a3b8;">${escapeHtml(data.companyName)}</p>
    </div>
  `;

  return container;
}

/**
 * Renders the receipt as HTML (with Arabic-capable fonts) then captures to PDF.
 * Vector Helvetica text corrupts Arabic; browser layout matches the portal modal.
 */
export async function generateReceiptPdf(
  data: ReceiptPdfData,
  filename: string,
  options?: { isRTL?: boolean }
): Promise<void> {
  const isRTL = options?.isRTL ?? false;
  const element = buildReceiptElement(data, isRTL);
  document.body.appendChild(element);

  try {
    if (document.fonts?.ready) {
      await document.fonts.ready;
    }

    const canvas = await html2canvas(element, {
      scale: 2,
      useCORS: true,
      backgroundColor: "#ffffff",
      logging: false,
    });

    const imgData = canvas.toDataURL("image/png");
    const doc = new jsPDF({ unit: "mm", format: "a4", orientation: "portrait" });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const imgHeight = (canvas.height * pageWidth) / canvas.width;
    const finalHeight = Math.min(imgHeight, pageHeight);

    doc.addImage(imgData, "PNG", 0, 0, pageWidth, finalHeight, undefined, "FAST");
    doc.save(filename);
  } finally {
    document.body.removeChild(element);
  }
}
